import React, { useEffect } from "react";
import { ReactComponent as VectorImage } from "../../assets/images/module.svg";
import VectorDrop from "../../assets/images/module-arrow.png";
import SelfLearningSubmenu from "./SelfLearningSubmenu";
import "./SelfLearning.scss";
import { useStyles, useTranslations } from "examsbook-react-sdk/custom-hooks";
import { selfLearningVideoUrl, toggleBottomBar } from "examsbook-react-sdk/actions";
import { useDispatch } from "react-redux";
import { Box, Button, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "../../constants/themeProvider";
import { setDataId, setSectionId } from "examsbook-react-sdk/actions/getSelfLearningAction";
import { ReactComponent as GreenTick } from "../../assets/images/greenTick.svg";
import whiteTick from "../../assets/images/whiteTick.png";
import LockIcon from "@material-ui/icons/Lock";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { CircularProgressbar } from "react-circular-progressbar";
import PropTypes from "prop-types";
import { useTheme as useMuiTheme } from "@material-ui/core/styles";
import { setCourseName, setCourseProgress, setShowEvaluationTestAction } from "examsbook-react-sdk/actions/getCourses";

const SUPPORTED_MODELS = new Set(["RecordedVideo", "TextNotes", "PdfNotes"]);

function formatReadTime(minutes) {
  if (!minutes && minutes !== 0) return "";

  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hrs > 0 && mins > 0) {
    return `${hrs} hr ${mins} min`;
  }
  if (hrs > 0) {
    return `${hrs} hr`;
  }
  return `${mins} min`;
}

const getSectionItems = (section) => {
  if (!Array.isArray(section?.courseSectionData)) {
    return [];
  }
  return section.courseSectionData;
};

function processSectionDataForSelection({
  selectedIndex,
  sectionData,
  setSectionNumber,
  setIsDropDown,
  setSectionName,
  setNextClick,
  setCourseDataList,
}) {
  const sections = Array.isArray(sectionData?.data?.section)
    ? sectionData.data.section
    : [];

  for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx += 1) {
    if (sectionIdx < selectedIndex) {
      continue;
    }

    const section = sections[sectionIdx];
    const sectionItems = getSectionItems(section);

    setSectionNumber((prevState) => [...prevState, sectionItems.length]);

    setIsDropDown((prevState) => ({
      ...prevState,
      [sectionIdx]: sectionIdx === selectedIndex,
    }));

    if (sectionIdx === selectedIndex) {
      setSectionName(section?.title?.en);
      const sectionItemsForNext = [...sectionItems];
      setNextClick([
        {
          ...sectionItemsForNext[0],
          sectionId: section?._id,
          title: section?.title,
        },
      ]);
    }

    const mappedSectionItems = sectionItems.map((data1) => ({
      ...data1,
      sectionId: section?._id,
      title: section?.title,
    }));

    setCourseDataList((prevState) => [...prevState, ...mappedSectionItems]);
  }
}

function handleSubmenuAutoClickSelection({
  item,
  sectionIndex,
  sectionId,
  courseName,
  setCurrentDataIndex,
  setCurrentSectionIndex,
  setSelectedSubmenu,
  dispatch,
  setSectionChanged,
  handleUpdateCourseProgress,
  onPdfOpen,
  pdfOpen,
  openPdfViewer,
  onSubmenuClick,
}) {
  const courseId = item?.courseId || item?.section?.data?.courseId?._id;

  setCurrentDataIndex(0);
  setCurrentSectionIndex(sectionIndex);
  setSelectedSubmenu(item._id);
  dispatch(setSectionId(sectionId));
  dispatch(setDataId(item._id));
  dispatch(setCourseName(courseName));

  if (item.refModel === "RecordedVideo") {
    dispatch(selfLearningVideoUrl(item));
    setSectionChanged(false);
  } else if (item.refModel === "TextNotes" || item.refModel === "PdfNotes") {
    const progressData = {
      sectionId,
      dataId: item._id,
      percent: 100,
      courseId,
      isRead: true,
    };
    handleUpdateCourseProgress(progressData);

    if (item.refModel === "PdfNotes") {
      onPdfOpen(!pdfOpen);
      openPdfViewer(item?.contentData?.featured);
    } else {
      dispatch(selfLearningVideoUrl(item));
    }
  }

  if (onSubmenuClick) {
    onSubmenuClick(item);
  }
}

function toggleDropdownForSection({
  activeItem,
  event,
  index,
  isLocked,
  currentSectionIndex,
  setCurrentSectionIndex,
  processSectionData,
  setSectionChanged,
  setSelectedSubmenu,
  setCurrentDataIndex,
  setIsDropDown,
  courseName,
  handleSubmenuAutoClick,
}) {
  event.stopPropagation();
  if (isLocked) {
    return;
  }

  const isCurrentlyOpen = currentSectionIndex === index;
  if (isCurrentlyOpen) {
    return;
  }

  setCurrentSectionIndex(index);
  processSectionData(index);
  setSectionChanged(true);
  setSelectedSubmenu(null);
  setCurrentDataIndex(0);
  setIsDropDown((prev) => ({ ...prev, [index]: true }));

  const sectionDataArray = Array.isArray(activeItem?.data?.courseSectionData)
    ? activeItem.data.courseSectionData
    : [];

  const firstItem = sectionDataArray.find((item) => SUPPORTED_MODELS.has(item.refModel));
  if (!firstItem) {
    return;
  }

  setTimeout(() => {
    handleSubmenuAutoClick(firstItem, index, activeItem?.data._id, courseName);
  }, 100);
}

function handleSectionViewClick({
  index,
  isLocked,
  processSectionData,
  dispatch,
  courseProgress,
  setCurrentSectionIndex,
  setCurrentDataIndex,
  sectionId,
  courseName,
  setShowEvaluationTest,
}) {
  if (isLocked) {
    return;
  }

  processSectionData(index);
  dispatch(setCourseProgress(courseProgress));
  setCurrentSectionIndex(index);
  setCurrentDataIndex(0);
  dispatch(setSectionId(sectionId));
  dispatch(setCourseName(courseName));
  dispatch(setShowEvaluationTestAction(true));
  setShowEvaluationTest(true);
  dispatch(toggleBottomBar(true));
}

function EvaluationSectionView({
  classes,
  isLocked,
  currentSectionIndex,
  index,
  onToggleDropdown,
  data,
  t,
  sectionIndex,
  sectionData,
  courseId,
  handleUpdateCourseProgress,
  setSectionChanged,
  pdfOpen,
  onPdfOpen,
  activeItem,
  selectedSubmenu,
  setSelectedSubmenu,
  nextClick,
  sectionName,
  onSubmenuClick,
  setCurrentSectionIndex,
  currentDataIndex,
  setCurrentDataIndex,
  openPdfViewer,
  isCourseExpired,
}) {
  return (
    <Box>
      <Box
        style={{
          borderTop: '1px solid #e7e7e75d',
          borderBottom: '1px solid #e7e7e75d',
          minHeight: '20px',
          cursor: isLocked ? 'not-allowed' : 'pointer',
          padding: '5px',
          transition: '0.3s all linear',
          opacity: isLocked ? 0.6 : 1,
        }}
        onClick={onToggleDropdown}
      >
        <Box className="innerBox1">
          <Box className="button-box1">
            {isLocked ? (
              <LockIcon style={{ fontSize: '18px', color: '#9e9e9e' }} />
            ) : (
              <Box
                component="img"
                style={{ height: '8px', width: '14px' }}
                className={currentSectionIndex === index ? 'arrow-down' : 'arrow-up'}
                src={VectorDrop}
              />
            )}
          </Box>
          <Box className="card1">
            <Box className="content-box1">
              <p className={classes.genustext}>{data?.title?.en}</p>
            </Box>
          </Box>
          {isLocked ? (
            <Box className={classes.timeContent} style={{ color: '#9e9e9e' }}>
              <LockIcon style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '4px' }} />
              {t('Locked')}
            </Box>
          ) : (
            <Box className={classes.timeContent}>{formatReadTime(data?.sectionReadTimeCount)}</Box>
          )}
        </Box>
      </Box>

      {currentSectionIndex === index && !isLocked && (
        <Box className="dropdown-content">
          {Array.isArray(data?.courseSectionData)
            ? data.courseSectionData.map((item, itemIndex) => (
                <SelfLearningSubmenu
                  key={item?._id || `${sectionIndex}-${itemIndex}`}
                  index={itemIndex}
                  sectionIndex={sectionIndex}
                  data={item}
                  section={sectionData}
                  courseId={courseId}
                  handleUpdateCourseProgress={handleUpdateCourseProgress}
                  sectionId={data?._id}
                  setSectionChanged={setSectionChanged}
                  pdfOpen={pdfOpen}
                  onPdfOpen={onPdfOpen}
                  activeItem={activeItem}
                  selectedSubmenu={selectedSubmenu}
                  setSelectedSubmenu={setSelectedSubmenu}
                  nextClick={nextClick}
                  sectionName={sectionName}
                  onSubmenuClick={onSubmenuClick}
                  currentSectionIndex={currentSectionIndex}
                  setCurrentSectionIndex={setCurrentSectionIndex}
                  currentDataIndex={currentDataIndex}
                  setCurrentDataIndex={setCurrentDataIndex}
                  openPdfViewer={openPdfViewer}
                  isCourseExpired={isCourseExpired}
                />
              ))
            : null}
        </Box>
      )}
    </Box>
  );
}

function StandardSectionView({
  classes,
  isLocked,
  isMobile,
  onViewClick,
  data,
  t,
  theme,
}) {
  return (
    <Box className={classes.selfLearningBox} style={{ opacity: isLocked ? 0.6 : 1 }}>
      <Box className="outerBox1" style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }} onClick={onViewClick}>
        <Box className="innerBox" style={{ gap: isMobile ? '' : '30px' }}>
          <Box className="image-box" style={{ backgroundColor: isMobile && 'rgba(176, 190, 197, 0.31)' }}>
            {isLocked ? (
              <LockIcon style={{ fontSize: '30px', color: '#9e9e9e' }} />
            ) : (
              <VectorImage alt="Vector arrow" />
            )}
          </Box>
          <Box style={{ flex: '1' }}>
            <Box className="content-box">
              <p className={classes.genustext}>{data?.title?.en}</p>
            </Box>
          </Box>
          <Box className="button-box">
            {isLocked ? (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', color: '#9e9e9e' }}>
                <LockIcon style={{ fontSize: '20px' }} />
                <Typography variant="inherit" style={{ fontWeight: 500 }}>{t('Locked')}</Typography>
              </Box>
            ) : (
              <>
                <Box style={{ width: isMobile ? 'unset' : '80%' }}>
                  {data?.sectionPercent <= 99 && !isMobile && (
                    <Box>
                      <LinearProgressWithLabel value={data?.sectionPercent} width={20} />
                    </Box>
                  )}
                  {data?.sectionPercent > 99 && !isMobile && (
                    <Box className={classes.completedPercentageDiv}>
                      <GreenTick />
                      <Typography variant="inherit" className={classes.completedPercentageText}>{`${Math.round(data?.sectionPercent)}`}% {t('Completed')}</Typography>
                    </Box>
                  )}
                </Box>
                <Box className="minutes-read" style={{ width: isMobile ? 'unset' : '40%' }}>
                  <AccessTimeIcon className={classes.readTimeText} />
                  <Typography variant="inherit">{formatReadTime(data?.sectionReadTimeCount)}</Typography>
                </Box>
              </>
            )}
          </Box>
          {isMobile && !isLocked && (
            <Box className={classes.sectionPercentDiv}>
              <Button className={classes.selfLearning} variant="contained">
                {data?.sectionPercent <= 99 && isMobile && (
                  <Box style={{ display: 'flex', gap: '6px' }}>
                    <LinearProgressCircular value={data?.sectionPercent} width={20} />
                    <Typography variant="inherit" className={classes.completedPercentageText}>{`${Math.round(data?.sectionPercent)}`}% {t('Completed')}</Typography>
                  </Box>
                )}
                {data?.sectionPercent > 99 && isMobile && (
                  <Box className={classes.sectionPercentDiv}>
                    <GreenTick />
                    <Typography variant="inherit" className={classes.completedPercentageText}>{`${Math.round(data?.sectionPercent)}`}% {t('Completed')}</Typography>
                  </Box>
                )}
              </Button>
            </Box>
          )}
          {!isMobile && <ChevronRightIcon style={{ fill: '#0000008A', marginRight: '20px' }} />}
        </Box>
      </Box>
    </Box>
  );
}

EvaluationSectionView.propTypes = {
  classes: PropTypes.shape({
    genustext: PropTypes.string,
    timeContent: PropTypes.string,
  }).isRequired,
  isLocked: PropTypes.bool,
  currentSectionIndex: PropTypes.number,
  index: PropTypes.number,
  onToggleDropdown: PropTypes.func,
  data: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.shape({ en: PropTypes.string }),
    sectionReadTimeCount: PropTypes.number,
    courseSectionData: PropTypes.array,
  }),
  t: PropTypes.func,
  sectionIndex: PropTypes.number,
  sectionData: PropTypes.shape({
    data: PropTypes.shape({
      section: PropTypes.array,
    }),
  }),
  courseId: PropTypes.string,
  handleUpdateCourseProgress: PropTypes.func,
  setSectionChanged: PropTypes.func,
  pdfOpen: PropTypes.bool,
  onPdfOpen: PropTypes.func,
  activeItem: PropTypes.shape({
    data: PropTypes.shape({
      _id: PropTypes.string,
      courseSectionData: PropTypes.array,
    }),
  }),
  selectedSubmenu: PropTypes.string,
  setSelectedSubmenu: PropTypes.func,
  nextClick: PropTypes.array,
  sectionName: PropTypes.string,
  onSubmenuClick: PropTypes.func,
  setCurrentSectionIndex: PropTypes.func,
  currentDataIndex: PropTypes.number,
  setCurrentDataIndex: PropTypes.func,
  openPdfViewer: PropTypes.func,
  isCourseExpired: PropTypes.bool,
};

StandardSectionView.propTypes = {
  classes: PropTypes.shape({
    selfLearningBox: PropTypes.string,
    genustext: PropTypes.string,
    completedPercentageDiv: PropTypes.string,
    completedPercentageText: PropTypes.string,
    readTimeText: PropTypes.string,
    sectionPercentDiv: PropTypes.string,
    selfLearning: PropTypes.string,
  }).isRequired,
  isLocked: PropTypes.bool,
  isMobile: PropTypes.bool,
  onViewClick: PropTypes.func,
  data: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.shape({ en: PropTypes.string }),
    sectionReadTimeCount: PropTypes.number,
    sectionPercent: PropTypes.number,
  }),
  t: PropTypes.func,
  theme: PropTypes.object,
};

export default function SelfLearningCard({
  courseName,
  isDropdownOpen,
  setIsDropdownOpen,
  sectionIndex,
  currentSectionIndex,
  setCurrentSectionIndex,
  currentDataIndex,
  setCurrentDataIndex,
  selectedSubmenu,
  setSelectedSubmenu,
  activeIndex,
  nextClick,
  setActiveIndex,
  index,
  setCurrentActiveSection,
  currentActiveSection,
  setSectionChanged,
  pdfOpen,
  openPdfViewer,
  onPdfOpen,
  courseId,
  setShowEvaluationTest,
  showEvaluationTest,
  sectionName,
  courseDataList,
  isDropDown,
  setIsDropDown,
  sectionData,
  setSectionNumber,
  setNextClick,
  setCourseDataList,
  setSectionName,
  sectionNumber,
  handleUpdateCourseProgress,
  isCourseExpired,
  data,
  activeItem,
  onSubmenuClick,
}) {
  const t = useTranslations();
  const dispatch = useDispatch();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const theme = useTheme();

  const styles = {
    genustext: {
      color: "#000000DE",
      fontWeight: 400,
      margin: 0,
      fontSize: isMobile ? "14px" : "16px",
      textTransform: 'capitalize',
      "@media (max-width: 959px)": {
        margin: 'revert'
      }
    },
    timeContent: {
      fontSize: "14px",
      fontWeight: 500,
      width: '40%',
      textAlign: 'right',
      color: '#000000B2'
    },
    selfLearningBox: {
      borderRadius: '4px',
      background: '#FFFFFF',
      margin: '15px 16px',
      display: 'flex',
      flexDirection: 'column',
    },
    selfLearning:{
      border: `1px solid ${theme?.primaryColor}`,
      color: `${theme?.primaryColor}`,
      fontWeight: '600',
      textTransform: 'capitalize',
      backgroundColor: 'transparent',
      boxShadow:'inherit',
    },
    completedPercentageDiv: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    completedPercentageText: {
      color: '#2E7D32',
      fontWeight: '600'
    },
    readTimeText: {
      fill: '#616161',
      height: '20px',
      width: '20px'
    },
    sectionPercentDiv: {
      width: '-webkit-fill-available',
      background: '#eff2f3',
      gap: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
  };

  const classes = useStyles(styles)();

  const processSectionData = (selectedIndex) => {
    processSectionDataForSelection({
      selectedIndex,
      sectionData,
      setSectionNumber,
      setIsDropDown,
      setSectionName,
      setNextClick,
      setCourseDataList,
    });
  };

  useEffect(() => {
    if (currentSectionIndex !== index) {
      setIsDropDown((prevState) => ({
        ...prevState,
        [index]: false,
      }));
    }
  }, [currentSectionIndex, index, setIsDropDown]);

  const handleSubmenuAutoClick = (item, nextSectionIndex, sectionId, nextCourseName) => {
    handleSubmenuAutoClickSelection({
      item,
      sectionIndex: nextSectionIndex,
      sectionId,
      courseName: nextCourseName,
      setCurrentDataIndex,
      setCurrentSectionIndex,
      setSelectedSubmenu,
      dispatch,
      setSectionChanged,
      handleUpdateCourseProgress,
      onPdfOpen,
      pdfOpen,
      openPdfViewer,
      onSubmenuClick,
    });
  };

  const isLocked = data?.locked === true;

  const toggleDropdown = (nextActiveItem, event, nextIndex) => {
    toggleDropdownForSection({
      activeItem: nextActiveItem,
      event,
      index: nextIndex,
      isLocked,
      currentSectionIndex,
      setCurrentSectionIndex,
      processSectionData,
      setSectionChanged,
      setSelectedSubmenu,
      setCurrentDataIndex,
      setIsDropDown,
      courseName,
      handleSubmenuAutoClick,
    });
  };

  function handleViewClick(nextIndex, courseProgress, sectionId) {
    handleSectionViewClick({
      index: nextIndex,
      isLocked,
      processSectionData,
      dispatch,
      courseProgress,
      setCurrentSectionIndex,
      setCurrentDataIndex,
      sectionId,
      courseName,
      setShowEvaluationTest,
    });
  }

  if (showEvaluationTest) {
    return (
      <EvaluationSectionView
        classes={classes}
        isLocked={isLocked}
        currentSectionIndex={currentSectionIndex}
        index={index}
        onToggleDropdown={(event) => toggleDropdown({ data }, event, index)}
        data={data}
        t={t}
        sectionIndex={sectionIndex}
        sectionData={sectionData}
        courseId={courseId}
        handleUpdateCourseProgress={handleUpdateCourseProgress}
        setSectionChanged={setSectionChanged}
        pdfOpen={pdfOpen}
        onPdfOpen={onPdfOpen}
        activeItem={activeItem}
        selectedSubmenu={selectedSubmenu}
        setSelectedSubmenu={setSelectedSubmenu}
        nextClick={nextClick}
        sectionName={sectionName}
        onSubmenuClick={onSubmenuClick}
        setCurrentSectionIndex={setCurrentSectionIndex}
        currentDataIndex={currentDataIndex}
        setCurrentDataIndex={setCurrentDataIndex}
        openPdfViewer={openPdfViewer}
        isCourseExpired={isCourseExpired}
      />
    );
  }

  return (
    <StandardSectionView
      classes={classes}
      isLocked={isLocked}
      isMobile={isMobile}
      onViewClick={() => handleViewClick(index, activeItem?.sectionData?.data?.progressPercent, data?._id)}
      data={data}
      t={t}
      theme={theme}
    />
  );
}

 function LinearProgressWithLabel({ value = 0 }) {
  const t = useTranslations();
  const theme = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const overallProgress = Math.round(value);

  return (
    <Box width="100%" display="flex" alignItems="center" sx={{ gap: isMobile ? "6px" : '10px', justifyContent: isMobile && 'center' }}>
      {overallProgress <= 99 ? (
        <>
          <Box width={isMobile ? "18px" : "24px"}>
            <CircularProgressbar value={overallProgress} styles={{ path: { stroke: isMobile ? theme?.primaryFontText :theme?.primaryColor, strokeWidth: '10px' } }}/>
          </Box>
          <Typography variant="inherit" sx={{ display: 'flex', color: '#000000DE' }}>
            {overallProgress}% {t('Completed')}
          </Typography>
        </>
      ) : (
        <Box style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
          {isMobile ? <Box component="img" src={whiteTick} alt="Completed" /> : <GreenTick/>}
          <Typography variant="inherit" style={{ color: isMobile ? '#fff' : '#2E7D32', fontWeight: '600'}}>{overallProgress}% {t('Completed')}</Typography>
        </Box>
      )}
    </Box>
  );
}

function LinearProgressCircular({ value = 0 }) {
  const theme = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const overallProgress = Math.round(value);

  return (
    <Box>
      {overallProgress <= 99 && (
        <Box width={isMobile ? "18px" : "24px"}>
          <CircularProgressbar value={overallProgress} styles={{ path: { stroke: isMobile && theme?.primaryColor, strokeWidth: '10px' } }}/>
        </Box>
      ) }
    </Box>
  );
}

LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
};

LinearProgressCircular.propTypes = {
    value: PropTypes.number.isRequired,
};

export { LinearProgressWithLabel, LinearProgressCircular };

SelfLearningCard.propTypes = {
  courseName: PropTypes.string,
  isDropdownOpen: PropTypes.bool,
  setIsDropdownOpen: PropTypes.func,
  sectionIndex: PropTypes.number,
  currentSectionIndex: PropTypes.number,
  setCurrentSectionIndex: PropTypes.func,
  currentDataIndex: PropTypes.number,
  setCurrentDataIndex: PropTypes.func,
  selectedSubmenu: PropTypes.string,
  setSelectedSubmenu: PropTypes.func,
  activeIndex: PropTypes.number,
  nextClick: PropTypes.array,
  setActiveIndex: PropTypes.func,
  index: PropTypes.number,
  setCurrentActiveSection: PropTypes.func,
  currentActiveSection: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setSectionChanged: PropTypes.func,
  pdfOpen: PropTypes.bool,
  openPdfViewer: PropTypes.func,
  onPdfOpen: PropTypes.func,
  courseId: PropTypes.string,
  setShowEvaluationTest: PropTypes.func,
  showEvaluationTest: PropTypes.bool,
  sectionName: PropTypes.string,
  courseDataList: PropTypes.array,
  isDropDown: PropTypes.object,
  setIsDropDown: PropTypes.func,
  sectionData: PropTypes.shape({
    data: PropTypes.shape({
      section: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          title: PropTypes.shape({ en: PropTypes.string }),
          courseSectionData: PropTypes.array,
        })
      ),
    }),
  }),
  setSectionNumber: PropTypes.func,
  setNextClick: PropTypes.func,
  setCourseDataList: PropTypes.func,
  setSectionName: PropTypes.func,
  sectionNumber: PropTypes.array,
  handleUpdateCourseProgress: PropTypes.func,
  isCourseExpired: PropTypes.bool,
  data: PropTypes.shape({
    _id: PropTypes.string,
    locked: PropTypes.bool,
    title: PropTypes.shape({ en: PropTypes.string }),
    sectionReadTimeCount: PropTypes.number,
    sectionPercent: PropTypes.number,
    courseSectionData: PropTypes.array,
  }),
  activeItem: PropTypes.shape({
    data: PropTypes.shape({
      _id: PropTypes.string,
      courseSectionData: PropTypes.array,
    }),
    sectionData: PropTypes.shape({
      data: PropTypes.shape({
        progressPercent: PropTypes.number,
      }),
    }),
  }),
  onSubmenuClick: PropTypes.func,
};